import type { CaptureContext } from "./sentry";

// The browser's way in to error reporting, and the reason it is not simply the
// tracker module itself.
//
// Two things the obvious version puts on the critical path, and neither buys
// the visitor anything until something has already gone wrong:
//
//  - The tracker SDK, a couple of hundred kilobytes the framework would have to
//    download, parse and run before the page is interactive.
//  - The public runtime configuration. On a prerendered marketing page there is
//    no rendered document carrying those values, so reading them costs a
//    network round trip, taken before hydration, on the first page a visitor
//    lands on.
//
// Together they delayed hydration measurably. So this module reads neither
// until it has to: it mints the identifier the visitor will be shown, hands
// back control, and fetches the configuration and the SDK afterwards. The
// failure is recorded a tick later than it happened, which costs nothing; the
// page is interactive sooner for everybody who never sees a failure at all.

// What this reaches the outside world through. Production leaves them at their
// defaults — two dynamic imports and the platform's own id generator — and a
// test substitutes all three, so the behaviour is exercised without a browser,
// a bundler or an SDK.
export interface ReportingDeps {
  readonly loadEnv?: () => Promise<Record<string, string | undefined>>;
  readonly loadTracker?: () => Promise<TrackerModule>;
  readonly newEventId?: () => string;
}

// Only the part of the tracker module this needs, so the heavy one is not
// imported for its types either.
export interface TrackerModule {
  initSentry(options: {
    dsn: string | undefined;
    env: Record<string, string | undefined>;
    appOwnsGlobalHandlers?: boolean | undefined;
  }): boolean;
  captureError(error: unknown, context?: CaptureContext, eventId?: string): string | undefined;
}

export interface BrowserReporter {
  // Records a failure and returns the id the visitor may quote.
  //
  // Undefined means there is genuinely nothing to quote: either no tracker is
  // configured, or this is the first failure of the page and it beat the
  // configuration being read. In that second case the failure is still
  // recorded — under the tracker's own id — because showing a string that
  // resolves to nothing would be worse than showing none at all.
  report(error: unknown, context?: CaptureContext): string | undefined;
  // Reads the configuration ahead of time, so a later failure can answer with
  // an identifier immediately. Called once the page has gone quiet.
  warm(): Promise<void>;
}

// The tracker's own identifier format: 32 hex characters, no dashes. Minted
// here so the error page can show it at once, and handed to the SDK so the
// event is filed under the same string.
function randomEventId(): string {
  return crypto.randomUUID().replaceAll("-", "");
}

export function createBrowserReporter(deps: ReportingDeps = {}): BrowserReporter {
  const loadEnv = deps.loadEnv ?? (async () => (await import("$env/dynamic/public")).env);
  const loadTracker = deps.loadTracker ?? (() => import("./sentry"));
  const newEventId = deps.newEventId ?? randomEventId;

  // The configuration, once something has asked for it. `undefined` while it
  // has not been read; `null` once read and found absent, which is an answer.
  let dsn: string | null | undefined;
  let publicEnv: Record<string, string | undefined> = {};
  let loading: Promise<string | null> | undefined;

  async function readConfig(): Promise<string | null> {
    if (dsn !== undefined) return dsn;
    loading ??= (async () => {
      try {
        const env = await loadEnv();
        publicEnv = {
          // The shared reader names the release the same way in every process,
          // which is what makes one deploy one release across the product. A
          // browser has no process environment, so the deployment republishes
          // the values it needs under the public prefix.
          SENTRY_RELEASE: env.PUBLIC_SENTRY_RELEASE,
          SENTRY_ENVIRONMENT: env.PUBLIC_SENTRY_ENVIRONMENT,
          GIT_SHA: env.PUBLIC_GIT_SHA,
        };
        dsn = env.PUBLIC_SENTRY_DSN?.trim() || null;
      } catch {
        // No configuration reachable means no tracker, which is the same
        // answer as none being configured: report nothing, and say so.
        dsn = null;
      }
      return dsn;
    })();
    return loading;
  }

  return {
    async warm() {
      await readConfig();
    },

    report(error, context = {}) {
      const known = dsn;
      const eventId = known ? newEventId() : undefined;

      void (async () => {
        try {
          const configured = known ?? (await readConfig());
          if (!configured) return;
          const tracker = await loadTracker();
          tracker.initSentry({
            dsn: configured,
            env: publicEnv,
            appOwnsGlobalHandlers: true,
          });
          tracker.captureError(error, context, eventId);
        } catch {
          // The configuration or the SDK could not be fetched — an offline
          // visitor, a blocked request, a deploy that swapped the assets under
          // this page. Nothing useful remains to be done: the visitor has
          // already been told about the failure, and throwing here would
          // replace it with a second one.
        }
      })();

      return eventId;
    },
  };
}

const browserReporter = createBrowserReporter();

export const reportError: BrowserReporter["report"] = (error, context) =>
  browserReporter.report(error, context);

export const warmReporting: BrowserReporter["warm"] = () => browserReporter.warm();
